import {
    AirFare,
    AirFareOption,
    AirParams,
    FareOptionsResponse,
    GatewayParam,
    SingleFareOptions,
    VsOption,
} from "@ssc-booking/api-contracts"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as O from "fp-ts/Option"
import * as P from "fp-ts/Predicate"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import * as ORD from "fp-ts/Ord"
import * as N from "fp-ts/number"
import { match } from "ts-pattern"
import { FareCode } from "@silversea-ssc/web-fares-fp"
import { farePriority } from "@silversea-ssc/web-fares"
import { Option } from "fp-ts/Option"
import { selectDefaultGateway } from "../gateway"
import { fareCodeLabel } from "../../../../_common/format/fare-code-label"
import { FareContent } from "../../../../_common/sanity/sanity-types"
import { FareCard, FareCardIndex, FareCardKey } from "./fare-card"
import { FeasibleAirFares } from "./feasible-air-fares"

export const toFareCards = (faresContent: FareContent[], fareOptions: FareOptionsResponse) =>
    F.pipe(
        fareOptions.options,
        ROA.sort(byFarePriority),
        ROA.chain(validFlightConfigsForFare),
        ROA.map(toFareCardInputs),
        ROA.mapWithIndex(toUnknownFareCard(faresContent)),
    )

type FlightsOrCruiseOnly = { type: "flightsOrCruiseOnly"; airFareOption: AirFareOption }
type FlightNotAvailable = { type: "flightNotAvailable" }
type FlightConfig = FlightsOrCruiseOnly | FlightNotAvailable

export const validFlightConfigsForFare = (fareOption: SingleFareOptions) =>
    F.pipe(
        fareOption.airFares,
        ROA.filter(promotionalOrIncludedFareOrCruiseOnly),
        keepCruiseOnlyAndCheapestFlight(fareOption),
        ROA.map(extendWithAirParams),
        ifNoAirFareThenUseNotAvailable,
        ROA.map((flightConfig): [SingleFareOptions, FlightConfig] => [fareOption, flightConfig]),
    )

const promotionalOrIncludedFareOrCruiseOnly = F.pipe(
    AirFareOption.hasAirFare("CruiseOnly"),
    P.or(AirFareOption.isPromotional),
    P.or(AirFareOption.isIncluded),
)

const keepCruiseOnlyAndCheapestFlight = (fareOption: SingleFareOptions) =>
    F.flow(ROA.sort(airFaresByAirFare), ROA.takeLeft(howManyAirFaresShouldWeTake(fareOption)))
const airFaresByAirFare = F.pipe(
    AirFare.PriorityOrd,
    ORD.reverse,
    ORD.contramap(AirFareOption.lensFromProp("airFare").get),
)
const howManyAirFaresShouldWeTake = (fareOption: SingleFareOptions) => (isCruiseOnlyAvailable(fareOption) ? 2 : 1)
const isCruiseOnlyAvailable = (fareOption: SingleFareOptions): boolean =>
    F.pipe(
        fareOption.airFares,
        ROA.filter(AirFareOption.hasAirFare("CruiseOnly")),
        ROA.head,
        O.fold(F.constFalse, F.constTrue),
    )

const extendWithAirParams = (airFareOption: AirFareOption): FlightConfig => ({
    airFareOption,
    type: "flightsOrCruiseOnly",
})

const ifNoAirFareThenUseNotAvailable = F.flow(
    (x: readonly FlightConfig[]) => RONEA.fromReadonlyArray(x),
    O.getOrElse(() => RONEA.of(flightNotAvailable)),
)
const flightNotAvailable: FlightConfig = { type: "flightNotAvailable" }

type FareCardInputs = {
    options: SingleFareOptions
    feasibleAirFares: FeasibleAirFares
    air: AirParams
}

const toFareCardInputs = ([options, flightConfig]: [SingleFareOptions, FlightConfig]) =>
    match(flightConfig)
        .with(
            { type: "flightNotAvailable" },
            (): FareCardInputs => ({
                options,
                feasibleAirFares: FeasibleAirFares.noIncluded(),
                air: AirParams.notAvailable,
            }),
        )
        .with(
            { type: "flightsOrCruiseOnly" },
            ({ airFareOption }): FareCardInputs => ({
                options,
                feasibleAirFares: FeasibleAirFares.from(airFareOption, options.airFares),
                air: airWithDefaultGateway(options, airFareOption),
            }),
        )
        .exhaustive()

const airWithDefaultGateway = (options: SingleFareOptions, currentAirFareOption: AirFareOption) => {
    const airFare = currentAirFareOption.airFare
    if (airFare === "CruiseOnly") return AirParams.of.cruiseOnly({})
    return AirParams.of.flight({
        airClass: airFare,
        gateway: defaultGateway(options),
    })
}
const defaultGateway = (options: SingleFareOptions) =>
    F.pipe(
        selectDefaultGateway(options.gateways),
        O.fromNullable,
        O.fold(
            () => GatewayParam.countryDefault,
            (x) => GatewayParam.of.userSelected({ gateway: x.code }),
        ),
    )

export const fareCardKey = (fareCode: FareCode, hasFlights: boolean) =>
    `${fareCode}-${hasFlights ? "flights" : "no-flights"}` as FareCardKey //This value is built only here

export const vsEligibility = (vsOption: Option<VsOption>) =>
    F.pipe(vsOption, O.fold(F.constant("non-eligible" as const), F.constant("eligible" as const)))

const toUnknownFareCard =
    (faresContent: FareContent[]) =>
    (index: number, { options, feasibleAirFares, air }: FareCardInputs): FareCard => ({
        key: fareCardKey(options.fareCode, FeasibleAirFares.hasFlights(feasibleAirFares)),
        index: index as FareCardIndex, //This value is built only here
        fare: options.fareCode,
        air,
        options,
        feasibleAirFares,
        status: "unknown",
        vsEligibility: vsEligibility(options.vs),
        fareLabel: fareCodeLabel(faresContent, options.fareCode),
        suitePrices: [],
    })

const byFarePriority = F.pipe(
    N.Ord,
    ORD.contramap((x: SingleFareOptions) => farePriority[x.fareCode]),
)
